import React, { useState } from 'react';
import { array, func, number, object, string } from 'prop-types';
import { connect } from 'react-redux';
import template from 'url-template';
import { withRouter } from 'react-router-dom';

import { createProfileObject, fetchProfileObjects, resetProfileObjects } from '../../actions/profileObjectActions';
import { fetchProfiles } from '../../actions/profileActions';
import { fetchBasAgent } from '../../actions/basAgentActions';
import { handleNetworkError } from '../../actions/applicationMessageActions';
import { patchItem } from '../../helpers/collections';
import { updateInventory } from '../../actions/inventoryActions';
import useInventory from '../../hooks/useInventory';
import useSelectedAgent from '../../hooks/useSelectedAgent';
import useProfileObjects from '../../hooks/useProfileObjects';
import useProfiles from '../../hooks/useProfiles';
import EsmsPage from '../../components/EsmsPage';
import InventoryTable from '../../components/InventoryTable';
import ActionBar from '../../components/ActionBar';
import ActionBarItem from '../../components/ActionBarItem';

import Input from '@material-ui/core/Input';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';

import classes from './UseExistingProfilesPage.module.css';

const BasInventoryUrl = template.parse('/iothub/basagents/manage/inventory/niagara{?company,basAgent}');

const InitialProfile = Object.freeze({
    basType:   null,
    deviceIds: [],
    name:      ''
});

const UseExistingProfilesPage = ({
    companyInfo,
    createProfile,
    createProfileObject,
    devices,
    fetchProfileObjects,
    fetchBasAgent,
    handleNetworkError,
    history,
    location,
    patchItem,
    redirectHandler,
    resetProfileObjects,
    selectedOBIXPaths,
    treeCacheId,
    updateInventory
}) => {
    const [selectedProfile, setSelectedProfile] = useState('');
    const [profileId, setProfileId] = useState('');
    const [newProfile, setNewProfileObjects] = useState({ ...InitialProfile });
    const search = new URLSearchParams(location.search);
    const { basType, loading: loadingSelectedAgent, selectedAgent } =
        useSelectedAgent(search.get('basAgent'));
    const { profiles } = useProfiles();
    const { loading: loadingInventory, inventory } = useInventory();
    const { profileObjects } = useProfileObjects(profileId);

    const isLoading = loadingSelectedAgent || loadingInventory;

    const handleChange = async event => {
        const { id, name } = event.target.value;

        setProfileId(id);
        setSelectedProfile(name);

        await fetchProfileObjects(id);

        setNewProfileObjects({
            ...newProfile,
            profileId
        });
    };

    const redirectToBasInventory = () => {
        Object.entries(inventory).forEach(pair => {
            const [id, instance] = pair;
            if (!instance || !instance.selected) {
                return;
            }
            instance.selected = false;

            updateInventory(id, { profileName: selectedProfile, profileId });
        });
        resetProfileObjects();
        history.push(BasInventoryUrl.expand({
            company:  companyInfo.companyId,
            basAgent: selectedAgent.id,
            selectedOBIXPaths
        }));
    };

    const sorter = (a, b) => {
        let itemA = a.name.toUpperCase();
        let itemB = b.name.toUpperCase();

        if (itemA < itemB) {
            return -1;
        }
        if (itemA > itemB) {
            return 1;
        }

        return 0;
    };

    return (
        <EsmsPage
            child="Use Existing Profile"
            parents={[
                companyInfo.companyName,
                'BAS Agents',
                'Profiles'
            ]}
            title="Use Existing Profile"
        >
            <Select
                value={selectedProfile || 'Select Profile'}
                id="profiles"
                className={classes.profileSelect}
                input={<Input id="select-multiple-checkbox" />}
                renderValue={selected => selected}
                onChange={handleChange}
                name="profiles"
                inputProps={{
                    name: 'profiles',
                    id:   'profiles'
                }}
            >
                {profiles
                    .filter(profile => profile.basType === basType)
                    .sort(sorter)
                    .map(profile => (
                        <MenuItem
                            key={profile.id}
                            value={profile}
                        >
                            <span>{profile.name}</span>
                        </MenuItem>
                    ))}
            </Select>
            <h1 className={classes.tableHeader}>Profile Objects</h1>
            <ActionBar>
                <ActionBarItem
                    disabled={!profileObjects.length}
                    onClick={redirectToBasInventory}
                    type="save"
                >
                    Assign
                </ActionBarItem>
            </ActionBar>
            <InventoryTable
                inventory={profileObjects}
                loading={isLoading}
                rootId={selectedOBIXPaths}
                treeCacheId={treeCacheId}
                type="existingProfile"
            />
        </EsmsPage>
    );
};

UseExistingProfilesPage.defaultProps = {
    fetchBasAgent:       () => {},
    fetchProfiles:       () => {},
    fetchProfileObjects: () => {},
    resetProfileObjects: () => {},
    fetchInventory:      () => {},
    handleNetworkError:  () => {},
    patchItem,
    updateInventory:     () => {}
};

UseExistingProfilesPage.propTypes = {
    companyInfo:            object,
    createProfile:          func,
    fetchBasAgent:          func,
    fetchInventory:         func,
    fetchProfiles:          func,
    fetchProfileObjects:    func,
    handleNetworkError:     func,
    inventory:              object,
    patchItem:              func,
    resetProfileOBjects:    func,
    selectedAgent:          object,
    selectedProfile:        string,
    selectedProfileObjects: object,
    selectedInventory:      object,
    selectedOBIXPaths:      array,
    treeCacheId:            number.isRequired,
    updateInventory:        func
};

export const mapStateToProps = ({ companyInfo, inventory, selectedOBIXPaths, ...state }) => {
    const {
        treeCacheId
    } = inventory;
    return {
        companyInfo,
        devices: state.device.devices,
        selectedOBIXPaths,
        treeCacheId
    };
};

const mapDispatchToProps = {
    createProfileObject,
    fetchBasAgent,
    fetchProfiles,
    fetchProfileObjects,
    resetProfileObjects,
    handleNetworkError,
    updateInventory
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(UseExistingProfilesPage));
